/**
 * The clock anchor, persisted (D-10 + `TRACK-D` "Clock anchoring").
 *
 * `@saniplus/api-client` owns the in-memory anchor and updates it from
 * `meta.serverTime` on every response. It deliberately does **not** persist:
 * "Persisting it across restarts is the worker PWA's job (IndexedDB
 * `clockAnchor`), because only the punch queue needs it to survive a cold
 * start." This module is that job.
 *
 * ## The restart case
 *
 * `performance.now()` restarts with the JS context, so after a cold start the
 * monotonic chain is broken and `monotonicElapsedMs` cannot be computed
 * honestly. The payload is therefore **marked** by omitting the field: the
 * server falls back to `deviceRecordedAt` and always raises `CLOCK_DRIFT`,
 * which is correct and far better than shipping a reconstructed number that
 * looks like a measurement. `clockOffsetMs` and `lastServerContactAt` survive,
 * so the punch still carries the last real server reading it was made against.
 *
 * All arithmetic here is on instants (epoch ms). Business dates and anything
 * calendar-shaped belong to the site timezone via the api-client formatters —
 * never to fixed-millisecond maths (D-13, DST).
 */
import { getClockAnchor, recordServerTime, serverNow } from '@saniplus/api-client';

import {
  readClockAnchor,
  writeClockAnchor,
  type PersistedClockAnchor,
  type QueuedCapturedAt,
} from './db';

/** The anchor read back from IndexedDB at boot, before any server contact. */
let restored: PersistedClockAnchor | null = null;

type ServerContactListener = () => void;
const serverContactListeners = new Set<ServerContactListener>();

/**
 * Every successful response passes through here, which makes it the honest
 * place to hang "we are online again". The scheduler subscribes; nothing else
 * has to know when a request succeeded. (Listener, not a direct call into the
 * scheduler, so this module keeps no dependency on the queue.)
 */
export function onServerContact(listener: ServerContactListener): () => void {
  serverContactListeners.add(listener);
  return (): void => {
    serverContactListeners.delete(listener);
  };
}

function monotonicNow(): number {
  return typeof performance === 'undefined' ? Date.now() : performance.now();
}

/**
 * Wire this to `configureApiClient({ onServerTime })` in `main.tsx` instead of
 * the api-client's bare `recordServerTime`: it does the same thing and then
 * persists the anchor, so the next cold start still knows the offset.
 *
 * The write is fire-and-forget. A failing IndexedDB write must never turn a
 * successful response into a failed one, and there is nothing useful to say
 * about it on a phone with no console.
 */
export function persistServerTime(serverTime: string): void {
  recordServerTime(serverTime);
  const anchor = getClockAnchor();
  if (anchor === null) return;
  // A live anchor supersedes the restored one from here on.
  restored = null;
  void writeClockAnchor({
    offsetMs: anchor.offsetMs,
    monotonicBaseMs: anchor.monotonicBaseMs,
    wallClockBaseMs: anchor.wallClockBaseMs,
    lastServerContactAt: anchor.lastServerContactAt,
  }).catch(() => undefined);

  for (const listener of serverContactListeners) {
    try {
      listener();
    } catch {
      // A flush must never turn a successful response into a failure.
    }
  }
}

/** Boot step: recover the last known offset before the first response lands. */
export async function restoreClockAnchor(): Promise<PersistedClockAnchor | null> {
  if (getClockAnchor() !== null) return null;
  const stored = await readClockAnchor();
  restored = stored;
  return stored;
}

/**
 * True when the only anchor available came off disk — i.e. this JS context has
 * not talked to the server yet. Punches captured now carry the mark.
 */
export function isAnchorReconstructed(): boolean {
  return getClockAnchor() === null && restored !== null;
}

/** The last known anchor, live or restored. `null` before either exists. */
export function currentAnchor(): PersistedClockAnchor | null {
  const live = getClockAnchor();
  if (live !== null) {
    return {
      offsetMs: live.offsetMs,
      monotonicBaseMs: live.monotonicBaseMs,
      wallClockBaseMs: live.wallClockBaseMs,
      lastServerContactAt: live.lastServerContactAt,
    };
  }
  return restored;
}

/**
 * "Now" on the server's clock. After a restart the monotonic base is gone, so
 * the restored offset is applied to the wall clock — still better than a raw
 * device clock, which is the thing D-10 exists because we cannot trust.
 */
export function now(): number {
  if (getClockAnchor() !== null) return serverNow();
  if (restored !== null) return Date.now() + restored.offsetMs;
  return Date.now();
}

/** "Now" as an ISO instant on the server's clock. */
export function nowIso(): string {
  return new Date(now()).toISOString();
}

/** Is the device claiming to be online? Advisory — the request is the proof. */
export function isOnline(): boolean {
  return typeof navigator === 'undefined' ? true : navigator.onLine !== false;
}

/** Elapsed ms on the server's clock since a server-confirmed instant. */
export function elapsedSince(serverInstantIso: string, at: number = now()): number {
  const started = Date.parse(serverInstantIso);
  if (Number.isNaN(started)) return 0;
  return Math.max(0, at - started);
}

/**
 * The `capturedAt` evidence for a punch payload, or `null` when this device
 * has never seen a server time — in which case the request omits `capturedAt`
 * entirely (the contract makes it optional) rather than inventing an anchor.
 */
export function buildCapturedAt(): QueuedCapturedAt | null {
  const deviceRecordedAt = new Date().toISOString();
  const live = getClockAnchor();
  if (live !== null) {
    return {
      deviceRecordedAt,
      monotonicElapsedMs: Math.max(0, Math.round(monotonicNow() - live.monotonicBaseMs)),
      clockOffsetMs: Math.round(live.offsetMs),
      lastServerContactAt: live.lastServerContactAt,
    };
  }
  if (restored !== null) {
    // Cold start: `monotonicElapsedMs` is deliberately omitted (see above).
    return {
      deviceRecordedAt,
      clockOffsetMs: Math.round(restored.offsetMs),
      lastServerContactAt: restored.lastServerContactAt,
    };
  }
  return null;
}

/** Test-only: forget the restored anchor between cases. */
export function resetOfflineClock(): void {
  restored = null;
}
